import {
  Component,
  Input,
  Output,
  EventEmitter,
  HostListener,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import {
  ApplicationStatus,
  APPLICATION_STATUSES,
} from '../../models/application-status.model';
import { TranslationService } from '../../services/translation.service';
import type { FilterPanelTranslations } from '../../i18n/filter-panel';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface FilterPanelConfig {
  defaultExpanded?: boolean;
  panelClass?: string;
  headerClass?: string;
  showExportButton?: boolean;
  showSearchField?: boolean;
  showStatusFilter?: boolean;
  showCompanyFilter?: boolean;
  showLanguageFilter?: boolean;
  showKeywordFilter?: boolean;
  showDocumentFilter?: boolean;
  // Data inputs
  companies?: string[];
  languages?: string[];
  keywords?: string[];
  applications?: any[]; // Full application data
  showClearButton?: boolean;
  /** Custom CSS class for the filter section */
  filterSectionClass?: string;
  /** Custom CSS class for the filter field */
  filterFieldClass?: string;
  /** Custom CSS class for the clear button */
  clearButtonClass?: string;
  /** Whether to show the total count */
  showTotalCount?: boolean;
  /** Custom label for the total count */
  totalCountLabel?: string;
  /** Whether to show the filtered count */
  showFilteredCount?: boolean;
  /** Custom label for the filtered count */
  filteredCountLabel?: string;
  /** Whether to show the selected count */
  showSelectedCount?: boolean;
  /** Custom label for the selected count */
  selectedCountLabel?: string;
}

export interface FilterSection<T = string> {
  id: string;
  title: string;
  type: 'single-select' | 'multi-select' | 'text' | 'range';
  options?: T[];
  selected?: T[];
  isExpanded?: boolean;
  isLocked?: boolean;
  ariaLabel?: string;
  description?: string;
}

export interface FilterChangeData {
  sectionId: string;
  selected: ApplicationStatus[];
  searchQuery?: string;
  companies?: string[];
  languages?: string[];
  keywords?: string[];
  hasCV?: boolean | null;
  hasCoverLetter?: boolean | null;
  /** AI Profile Match minimum threshold (0-100), null = no filter. */
  minMatchScore?: number | null;
  filteredCount?: number;
}

@Component({
  selector: 'app-filter-panel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
  ],
  templateUrl: './filter-panel.component.html',
  styleUrls: ['./filter-panel.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    role: 'region',
    'aria-label': 'Filters',
    tabindex: '0',
  },
})
export class FilterPanelComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  /** Configuration for the filter panel */
  @Input() config: FilterPanelConfig = {
    defaultExpanded: true,
    showClearButton: true,
    showTotalCount: true,
    showFilteredCount: true,
    showSelectedCount: true,
    totalCountLabel: 'Total',
    filteredCountLabel: 'Filtered',
  };
  @Input() filteredApplications: any[] = [];
  /** Total number of items being filtered */
  @Input() totalCount: number = 0;
  @Input() filteredCount: number = 0;
  /** Distinct company/language values across the user's ENTIRE
   * application history (chamba-ai-backend-fastapi#287's /summary) - the
   * dropdown options these back must not depend on whatever page of
   * results happens to be currently loaded. */
  @Input() companies: string[] = [];
  @Input() languages: string[] = [];

  /** Event emitted when filters change */
  @Output() filtersChanged = new EventEmitter<FilterChangeData>();
  @Output() closePanel = new EventEmitter<void>();
  @Output() exportRequested = new EventEmitter<void>();

  searchQuery = '';
  documentsFilter: 'all' | 'available' | 'missing' = 'all';

  onExport() {
    this.exportRequested.emit();
  }

  // Initial sections configuration with accessibility attributes
  sections: FilterSection<ApplicationStatus>[] = [
    {
      id: 'status',
      title: 'Status',
      type: 'multi-select',
      options: Object.keys(APPLICATION_STATUSES) as ApplicationStatus[],
      selected: [],
      isExpanded: this.config?.defaultExpanded ?? true,
      isLocked: false,
      ariaLabel: 'Filter by application status',
      description: 'Select one or more statuses to filter applications',
    },
  ];

  translations: FilterPanelTranslations = {} as FilterPanelTranslations;

  get t(): FilterPanelTranslations {
    return this.translations;
  }

  // Track the status form control - initialized as non-null with empty array
  statusControl: FormControl<ApplicationStatus[]> = new FormControl<
    ApplicationStatus[]
  >([], { nonNullable: true });

  // Custom dropdown state
  selectedStatuses: ApplicationStatus[] = [];
  isStatusDropdownOpen = false;
  isCompanyDropdownOpen = false;
  isLanguageDropdownOpen = false;
  isKeywordDropdownOpen = false;
  isRestoring = false;

  // Additional filter states
  selectedCompanies: string[] = [];
  selectedLanguages: string[] = [];
  selectedKeywords: string[] = [];
  hasCV: boolean | null = null;
  hasCoverLetter: boolean | null = null;
  selectedDateRange: { start: Date | null; end: Date | null } | null = null;
  /** AI Profile Match minimum threshold. Presets rather than a free-form
   * slider - "Any / 70%+ / 90%+" mirrors the three tiers the Profile
   * Match indicator itself already uses (match-low/medium/high in
   * application-summary-card.component.css), so the filter and the
   * badge the user is scanning for describe the same thresholds. */
  readonly matchScorePresets: number[] = [70, 90];
  minMatchScore: number | null = null;

  // Status options for display
  get statusOptions() {
    return this.sections[0]?.options?.map(status => ({
      label: this.getStatusLabel(status),
      value: status
    })) || [];
  }

  // Company options - chamba-ai-backend-fastapi#287: sourced from the
  // server-aggregated /summary (this.companies, the user's ENTIRE
  // history), not from whatever page of applications happens to be
  // loaded. Falls back to config/sample data only when the summary
  // genuinely hasn't loaded yet (this.companies still empty).
  get companyOptions() {
    const source = this.companies.length > 0
      ? this.companies
      : this.config?.companies || ['Preply', 'Google', 'Microsoft', 'Amazon', 'Meta'];
    return Array.from(new Set(source)).sort().map(company => ({
      label: company,
      value: company
    }));
  }

  // Language options - same sourcing as companyOptions above. Previously
  // ALWAYS fell back to the hardcoded sample list (config.languages was
  // never actually bound from home.component.html), so this dropdown
  // never reflected real data until now.
  get languageOptions() {
    const source = this.languages.length > 0
      ? this.languages
      : this.config?.languages || ['English', 'Spanish', 'German', 'French'];
    return Array.from(new Set(source)).map(lang => ({
      label: lang,
      value: lang
    }));
  }

  // Keywords options (extracted from job_keywords)
  get keywordOptions() {
    const keywords = new Set<string>();
    // Use data from config or fallback to sample keywords
    const sourceKeywords = this.config?.keywords || [
      'Full-Stack Engineer', 'React', 'TypeScript', 'Python/Django', 
      'A/B experimentation', 'CI/CD', 'Proactive Problem-Solving',
      'Communication Skills', 'Ownership', 'Java/Spring', 'Node.js',
      'Ruby on Rails', 'Scala/Play', 'PHP', 'Go'
    ];
    sourceKeywords.forEach(keyword => keywords.add(keyword));
    return Array.from(keywords).map(keyword => ({
      label: keyword,
      value: keyword
    }));
  }

  selectedCountLabel(count: number): string {
    return this.t.N_SELECTED.replace('{count}', String(count));
  }

  // Get status display text
  getStatusLabel(status: ApplicationStatus): string {
    // Try to get translated status from filter panel translations first
    const statusKey = status.toUpperCase() as keyof FilterPanelTranslations;
    if (this.translations[statusKey]) {
      return this.translations[statusKey];
    }
    
    // Fallback to APPLICATION_STATUSES (should ideally be removed)
    return APPLICATION_STATUSES[status]?.label || status;
  }

  // Custom dropdown methods
  toggleStatusDropdown(): void {
    this.isStatusDropdownOpen = !this.isStatusDropdownOpen;
    this.cdr.detectChanges();
  }

  toggleCompanyDropdown(): void {
    this.isCompanyDropdownOpen = !this.isCompanyDropdownOpen;
    this.cdr.detectChanges();
  }

  toggleLanguageDropdown(): void {
    this.isLanguageDropdownOpen = !this.isLanguageDropdownOpen;
    this.cdr.detectChanges();
  }

  toggleKeywordDropdown(): void {
    this.isKeywordDropdownOpen = !this.isKeywordDropdownOpen;
    this.cdr.detectChanges();
  }

  toggleStatus(status: ApplicationStatus): void {
    const index = this.selectedStatuses.indexOf(status);
    if (index > -1) {
      this.selectedStatuses.splice(index, 1);
    } else {
      this.selectedStatuses.push(status);
    }
    // Update form control without emitting to avoid duplicate events
    this.statusControl.setValue(this.selectedStatuses, { emitEvent: false });
    this.cdr.detectChanges();
  }

  toggleCompany(company: string): void {
    const index = this.selectedCompanies.indexOf(company);
    if (index > -1) {
      this.selectedCompanies.splice(index, 1);
    } else {
      this.selectedCompanies.push(company);
    }
    this.cdr.detectChanges();
  }

  toggleLanguage(language: string): void {
    const index = this.selectedLanguages.indexOf(language);
    if (index > -1) {
      this.selectedLanguages.splice(index, 1);
    } else {
      this.selectedLanguages.push(language);
    }
    this.cdr.detectChanges();
  }

  toggleKeyword(keyword: string): void {
    const index = this.selectedKeywords.indexOf(keyword);
    if (index > -1) {
      this.selectedKeywords.splice(index, 1);
    } else {
      this.selectedKeywords.push(keyword);
    }
    this.cdr.detectChanges();
  }

  /** Explicit tri-state setters (Any / Available / Missing), driven by a
   * 3-button segmented control in the template rather than a checkbox -
   * a checkbox only has 2 native states, so the previous
   * null -> true -> !value cycle had no way to represent "explicitly
   * excluded" (false) as visually distinct from "don't care" (null), and
   * once toggled away from null there was no way back to it short of
   * Clear All. */
  setHasCV(value: boolean | null): void {
    this.hasCV = value;
    this.cdr.detectChanges();
  }

  setHasCoverLetter(value: boolean | null): void {
    this.hasCoverLetter = value;
    this.cdr.detectChanges();
  }

  setMinMatchScore(value: number | null): void {
    this.minMatchScore = this.minMatchScore === value ? null : value;
    this.cdr.detectChanges();
  }

  clearStatuses(): void {
    this.selectedStatuses = [];
    this.statusControl.setValue([], { emitEvent: false });
    this.cdr.detectChanges();
  }

  clearCompanies(): void {
    this.selectedCompanies = [];
    this.cdr.detectChanges();
  }

  clearLanguages(): void {
    this.selectedLanguages = [];
    this.cdr.detectChanges();
  }

  clearKeywords(): void {
    this.selectedKeywords = [];
    this.cdr.detectChanges();
  }

  // Apply filters and close panel
  applyFilters(): void {
    // Apply filters with current selections
    this.onSelectionChange('status', this.selectedStatuses);
    
    // Close the panel
    this.closePanel.emit();
  }

  // Close dropdown when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Element;
    
    // Check if click is inside any dropdown container
    const statusContainer = target.closest('.status-select-container');
    const companyContainer = target.closest('.company-select-container');
    const languageContainer = target.closest('.language-select-container');
    const keywordContainer = target.closest('.keyword-select-container');
    
    // Only close dropdowns if they are currently open and click is outside
    if (this.isStatusDropdownOpen && !statusContainer) {
      this.isStatusDropdownOpen = false;
    }
    if (this.isCompanyDropdownOpen && !companyContainer) {
      this.isCompanyDropdownOpen = false;
    }
    if (this.isLanguageDropdownOpen && !languageContainer) {
      this.isLanguageDropdownOpen = false;
    }
    if (this.isKeywordDropdownOpen && !keywordContainer) {
      this.isKeywordDropdownOpen = false;
    }
  }

  // Track screen width for responsive behavior
  isMobile = false;
  private readonly MOBILE_BREAKPOINT = 768;

  constructor(
    private translationService: TranslationService,
    private cdr: ChangeDetectorRef
  ) {
    // Check initial screen size
    this.checkScreenSize();

    const current = this.translationService.getFilterPanelTranslations();
    if (current) {
      this.translations = current;
    }
    this.applyTranslations();

    this.translationService.onChange(() => {
      const next = this.translationService.getFilterPanelTranslations();
      if (next) {
        this.translations = next;
      }
      this.applyTranslations();
    });

  }

  ngOnInit(): void {
    // Translation initialization is handled in constructor

    // Subscribe to status control changes to update local state only
    // Don't emit onSelectionChange here - let applyFilters handle it
    this.statusControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: ApplicationStatus[]) => {
        this.selectedStatuses = value || [];
        // Only emit if this is not from restoreFilters
        if (!this.isRestoring) {
          this.cdr.detectChanges();
        }
      });

    // Initialize selectedStatuses from form control
    this.selectedStatuses = this.statusControl.value || [];
    
    // Initialize all filter arrays to avoid undefined errors
    this.selectedCompanies = [];
    this.selectedLanguages = [];
    this.selectedKeywords = [];
    this.hasCV = null;
    this.hasCoverLetter = null;
    this.minMatchScore = null;

    // Initialize dropdown states
    this.isStatusDropdownOpen = false;
    this.isCompanyDropdownOpen = false;
    this.isLanguageDropdownOpen = false;
    this.isKeywordDropdownOpen = false;
    
    // Flag to prevent duplicate emissions during restore
    this.isRestoring = false;
  }

  // Method to restore filter state from parent component
  restoreFilters(filters: any): void {
    this.isRestoring = true;
    
    if (filters.statuses) {
      this.selectedStatuses = filters.statuses;
      this.statusControl.setValue(filters.statuses, { emitEvent: false });
    }
    if (filters.companies) {
      this.selectedCompanies = filters.companies;
    }
    if (filters.languages) {
      this.selectedLanguages = filters.languages;
    }
    if (filters.keywords) {
      this.selectedKeywords = filters.keywords;
    }
    if (filters.hasCV !== undefined && filters.hasCV !== null) {
      this.hasCV = filters.hasCV;
    }
    if (filters.hasCoverLetter !== undefined && filters.hasCoverLetter !== null) {
      this.hasCoverLetter = filters.hasCoverLetter;
    }
    if (filters.minMatchScore !== undefined && filters.minMatchScore !== null) {
      this.minMatchScore = filters.minMatchScore;
    }
    if (filters.searchQuery) {
      this.searchQuery = filters.searchQuery;
    }
    
    this.isRestoring = false;
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyTranslations(): void {
    // Defaults used by template (when consumer didn't override)
    this.config = {
      ...this.config,
      totalCountLabel: this.config.totalCountLabel || this.t.TOTAL,
      filteredCountLabel: this.config.filteredCountLabel || this.t.FILTERED,
    };

    // Section labels
    if (this.sections?.[0]) {
      this.sections[0].title = this.t.STATUS;
      this.sections[0].ariaLabel = this.t.FILTER_BY_STATUS;
      this.sections[0].description = this.t.SELECT_APPLICATION_STATUS;
    }
  }

  /**
   * Check the current screen size and update responsive flags
   */
  private checkScreenSize(): void {
    this.isMobile = window.innerWidth < this.MOBILE_BREAKPOINT;
  }

  /**
   * Handle window resize events
   */
  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    this.checkScreenSize();
  }

  onSelectionChange(sectionId: string, selected: ApplicationStatus[]): void {
    this.filtersChanged.emit({
      sectionId,
      selected,
      searchQuery: this.searchQuery,
      companies: this.selectedCompanies,
      languages: this.selectedLanguages,
      keywords: this.selectedKeywords,
      hasCV: this.hasCV,
      hasCoverLetter: this.hasCoverLetter,
      minMatchScore: this.minMatchScore,
      filteredCount: this.filteredCount,
    });
  }

  clearAllFilters(): void {
    this.selectedStatuses = [];
    this.statusControl.setValue([], { emitEvent: false });
    this.searchQuery = '';
    this.selectedCompanies = [];
    this.selectedLanguages = [];
    this.selectedKeywords = [];
    this.hasCV = null;
    this.hasCoverLetter = null;
    this.minMatchScore = null;
    this.applyFilters();
  }

  // Handle keyboard events for the filter panel
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.closePanel.emit();
    }
  }
}
